import { z } from 'zod';

/**
 * Extract the first JSON object/array from a model's stdout. Codex/LLM output
 * often wraps JSON in prose or ```json fences; this pulls the payload out.
 */
export function extractJson(raw: string): unknown {
  const trimmed = raw.trim();
  // Strip code fences.
  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/);
  const body = fence ? fence[1] : trimmed;

  // Try direct parse first.
  try {
    return JSON.parse(body);
  } catch {
    /* fall through */
  }
  // Find the first balanced { } or [ ] block.
  const start = body.search(/[[{]/);
  if (start === -1) throw new Error('No JSON found in model output.');
  const open = body[start];
  const close = open === '{' ? '}' : ']';
  let depth = 0;
  for (let i = start; i < body.length; i++) {
    if (body[i] === open) depth++;
    else if (body[i] === close) {
      depth--;
      if (depth === 0) {
        return JSON.parse(body.slice(start, i + 1));
      }
    }
  }
  throw new Error('Unbalanced JSON in model output.');
}

// --- Zod schemas to validate AI output before it reaches the rest of the app --

export const gradingSchema = z.object({
  rubricVersion: z.string().optional(),
  scores: z.record(z.number()),
  evidence: z.record(z.string()).optional().default({}),
  flags: z.array(z.string()).optional().default([]),
  feedback: z.string().optional().default(''),
  strengths: z.array(z.string()).optional().default([]),
  weaknesses: z.array(z.string()).optional().default([]),
  missingDetails: z.array(z.string()).optional().default([]),
  suggestedImprovedAnswer: z.string().optional().default(''),
  sentenceImprovements: z.array(z.string()).optional().default([]),
});

export const answerSchema = z.object({
  strongAnswer: z.string(),
  shortVersion: z.string(),
  longVersion: z.string(),
  bulletStructure: z.array(z.string()),
  whatNotToSay: z.array(z.string()),
  followUps: z.array(z.string()),
  personalDetailsUsed: z.array(z.string()).optional().default([]),
});

/** Per-field provenance for a school-fit claim (sourceUrl + confidence). */
const fitFieldSourceSchema = z.object({
  sourceUrl: z.string().catch(''),
  confidence: z.enum(['high', 'medium', 'low']).catch('medium'),
  lastCheckedAt: z.string().optional().default(''),
});

export const fitSchema = z.object({
  mission: z.string().optional().default(''),
  curriculumStyle: z.string().optional().default(''),
  clinicalEmphasis: z.string().optional().default(''),
  communityServiceFocus: z.string().optional().default(''),
  researchFocus: z.string().optional().default(''),
  location: z.string().optional().default(''),
  uniquePrograms: z.string().optional().default(''),
  interviewStyle: z.string().optional().default(''),
  commonQuestionThemes: z.array(z.string()).optional().default([]),
  // A malformed sources object shouldn't fail the whole fit lookup.
  sources: z.record(fitFieldSourceSchema).catch({}),
});

/** One dossier section: content + provenance. Malformed fields degrade safely. */
const dossierSectionSchema = z.object({
  content: z.string().catch(''),
  sourceUrl: z.string().catch(''),
  confidence: z.enum(['high', 'medium', 'low']).catch('low'),
});

const EMPTY_SECTION = { content: '', sourceUrl: '', confidence: 'low' as const };

/** School dossier: every section present, each with its own source metadata. */
export const dossierSchema = z.object({
  mission: dossierSectionSchema.catch(EMPTY_SECTION),
  curriculum: dossierSectionSchema.catch(EMPTY_SECTION),
  clinicalTimeline: dossierSectionSchema.catch(EMPTY_SECTION),
  communityClinics: dossierSectionSchema.catch(EMPTY_SECTION),
  researchCenters: dossierSectionSchema.catch(EMPTY_SECTION),
  studentOrganizations: dossierSectionSchema.catch(EMPTY_SECTION),
  interviewFormat: dossierSectionSchema.catch(EMPTY_SECTION),
  recentNews: dossierSectionSchema.catch(EMPTY_SECTION),
  questionsToAsk: z.array(z.string()).catch([]),
});

/** Red Flag Coach response: personalized plans for difficult topics. */
export const redFlagPlanSchema = z.object({
  topics: z
    .array(
      z.object({
        topic: z.string(),
        whyItMatters: z.string().catch(''),
        answerStructure: z.array(z.string()).catch([]),
        likelyFollowUps: z.array(z.string()).catch([]),
        doNotSay: z.array(z.string()).catch([]),
        suggestedPoints: z.array(z.string()).catch([]),
      }),
    )
    .catch([]),
  generalAdvice: z.string().catch(''),
});

/**
 * Research response: fit + reported questions. Enum-ish fields fall back to
 * their safe value instead of failing the whole batch over one bad label
 * (category is coerced separately in researchService against the app's list).
 */
export const researchedQuestionSchema = z.object({
  question: z.string(),
  category: z.string().catch(''),
  source: z.string().catch(''),
  sourceUrl: z.string().catch(''),
  sourceType: z.enum(['official', 'unofficial']).catch('unofficial'),
  year: z.union([z.string(), z.number().transform(String)]).catch(''),
  tags: z.array(z.string()).catch([]),
  confidence: z.enum(['high', 'medium', 'low']).catch('medium'),
  quality: z
    .enum(['high_value', 'common', 'school_specific', 'outdated', 'generic', 'duplicate', 'low_confidence'])
    .catch('common'),
});

export const researchSchema = z.object({
  fit: fitSchema,
  questions: z.array(researchedQuestionSchema).optional().default([]),
});

/**
 * Intake response: the raw ProfileInputs fields populated from the primary
 * application. Every field falls back to "" so one malformed value doesn't
 * discard the rest of the extraction.
 */
export const profileInputsSchema = z.object({
  resume: z.string().catch(''),
  personalStatement: z.string().catch(''),
  dentalExperience: z.string().catch(''),
  shadowingHours: z.string().catch(''),
  volunteering: z.string().catch(''),
  research: z.string().catch(''),
  leadership: z.string().catch(''),
  manualDexterity: z.string().catch(''),
  whyDentistry: z.string().catch(''),
  strengths: z.string().catch(''),
  weaknesses: z.string().catch(''),
  gapYear: z.string().catch(''),
  schoolList: z.string().catch(''),
  backgroundNotes: z.string().catch(''),
  redFlags: z.string().catch(''),
});

export const profileSchema = z.object({
  motivationForDentistry: z.string(),
  clinicalExposure: z.string(),
  leadershipExamples: z.string(),
  serviceExamples: z.string(),
  researchExamples: z.string(),
  manualDexterityExamples: z.string(),
  ethicalViewpoints: z.string(),
  weakAreas: z.string(),
  programFitArguments: z.record(z.string()).optional().default({}),
  answerMaterial: z.array(z.string()).optional().default([]),
  strengths: z.array(z.string()).optional().default([]),
  redFlags: z.array(z.string()).optional().default([]),
});
